import { shortAddress } from '@plug-core/utils/shortAddress'
import useConfig from '@plug-core/hooks/useConfig'
import {
  Avatar,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { TokenBalance } from '@plug-core/providers/BalanceProvider'
import { NFT } from '@plug-core/interfaces/common'
import React, { FC } from 'react'
import { HiQuestionMarkCircle } from 'react-icons/hi2'
import { formatNumber } from '@plug-core/utils/formatNumber'
import TokenAvatar from '@components/TokenAvatar'
import { getChainIcon } from '@plug-core/utils/getChainIcon'

interface Props {
  toAddress: string
  token?: TokenBalance
  amount?: string
  nft?: NFT
  isFirstTimeSend: boolean
  knownContact?: string
}

const SendDetails: FC<Props> = ({
  nft,
  amount,
  token,
  toAddress,
  isFirstTimeSend,
  knownContact,
}) => {
  const { nfts } = useConfig()
  const { t } = useTranslation()

  const renderSelectedNft = () => {
    if (!nft) return

    const collection = nfts.find((c) => c.principal_id.toString() === nft.collection)

    return (
      <Stack spacing={2}>
        <Paper component={ListItem}>
          <ListItemAvatar>
            <Avatar sx={{ width: 35, height: 35 }} src={collection?.icon || nft.url} />
          </ListItemAvatar>
          <ListItemText
            primary={collection?.name}
            secondary={`#${nft.standard === 'ordinal' ? nft.number : nft.index}`}
          />
        </Paper>
      </Stack>
    )
  }

  const renderAsset = () => {
    if (nft) return renderSelectedNft()

    const normalizedAmount = Number(amount) * 10 ** -token!.token.decimals

    return (
      <Stack direction="row" alignItems="center">
        <Stack position="relative">
          <TokenAvatar logo={token?.token.logo} size={44} />
          <Stack
            position="absolute"
            right={-2}
            bottom={-5}
            bgcolor="background.paper"
            padding={0.45}
            borderRadius={4}
          >
            <img src={getChainIcon(token?.token.chain!)} height={14} width={14} />
          </Stack>
        </Stack>
        <Typography variant="h6" ml={1}>
          {formatNumber(normalizedAmount, token!.token.symbol)}
        </Typography>
      </Stack>
    )
  }

  return (
    <Stack px={2} py={2} spacing={1}>
      {renderAsset()}
      <Stack direction="row" alignItems="center">
        <Typography
          variant="body2"
          bgcolor={(theme) => theme.palette.common.white}
          color={(theme) => theme.palette.background.default}
          borderRadius={2}
          display="flex"
          alignItems="center"
          justifyContent="center"
          width={44}
          py={0.5}
        >
          {t('send.to')}:
        </Typography>
        <Typography variant="h6" ml={1} color={knownContact && 'rainbow.blue'}>
          {knownContact || shortAddress(toAddress, { leftSize: 16 })}
        </Typography>
      </Stack>

      {isFirstTimeSend && (
        <Typography
          fontSize={14}
          textAlign="center"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          {t('send.firstTimeSend')}
        </Typography>
      )}
    </Stack>
  )
}

export default SendDetails
